/**
 * Shared submit-validation feedback for FNOL claim form steps (matches {@link ClaimIncidentStep}).
 * @module claims/claimFormValidationFeedback
 */
import { useCallback, useState, type ReactNode } from 'react'

/** Banner copy when Next/Submit is clicked with incomplete required fields. */
export const CLAIM_FORM_SUBMIT_SUMMARY_MESSAGE =
  'Please complete the highlighted fields before continuing.' as const

export const CLAIM_FORM_SUBMIT_SUMMARY_ALERT_CLASS =
  'm-0 rounded-lg border border-solid border-red-200 bg-red-50 px-[12px] py-[10px] font-body text-sm leading-sm text-red-800'

export const CLAIM_FORM_FIELD_ERROR_BORDER_CLASS = '!border-red-600'

export const CLAIM_FORM_FIELD_ERROR_RING_CLASS = 'rounded-md ring-2 ring-red-600'

/** Wraps Yes/No toggles, time pickers, or card lists when a choice is required. */
export const CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS = 'rounded-lg p-[1px] ring-2 ring-red-600'

export const CLAIM_FORM_FIELD_ERROR_MESSAGE_CLASS =
  'm-0 font-body text-xs leading-xs text-red-700'

/** Applies red border class when `hasError` is true. */
export function claimFormFieldClass(base: string, hasError: boolean): string {
  return hasError ? `${base} ${CLAIM_FORM_FIELD_ERROR_BORDER_CLASS}`.trim() : base
}

type ClaimFormSubmitSummaryAlertProps = { id?: string }

/** Top-of-form alert shown after a failed Next/Submit attempt. */
export function ClaimFormSubmitSummaryAlert({ id }: ClaimFormSubmitSummaryAlertProps) {
  return (
    <p id={id} role="alert" className={CLAIM_FORM_SUBMIT_SUMMARY_ALERT_CLASS}>
      {CLAIM_FORM_SUBMIT_SUMMARY_MESSAGE}
    </p>
  )
}

type ClaimFormFieldErrorProps = { id: string; children: ReactNode }

/** Inline message under a highlighted control. */
export function ClaimFormFieldError({ id, children }: ClaimFormFieldErrorProps) {
  return (
    <p id={id} role="alert" className={CLAIM_FORM_FIELD_ERROR_MESSAGE_CLASS}>
      {children}
    </p>
  )
}

/**
 * Tracks whether the user tried to proceed; exposes summary visibility and a guarded submit handler.
 */
export function useClaimFormSubmitAttempt(canProceed: boolean) {
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const showSubmitSummary = submitAttempted && !canProceed

  const attemptSubmit = useCallback(
    (onSuccess: () => void) => {
      if (!canProceed) {
        setSubmitAttempted(true)
        return
      }
      setSubmitAttempted(false)
      onSuccess()
    },
    [canProceed]
  )

  const clearSubmitAttempt = useCallback(() => setSubmitAttempted(false), [])

  return { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt, setSubmitAttempted }
}
